import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/utils/cn';

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-[9999] px-2 py-1 rounded bg-neutral-900 text-white text-xs whitespace-nowrap',
        'animate-[fadeIn_0.12s_ease-out]',
        className
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = 'TooltipContent';

/**
 * Simple Tooltip wrapper — drop-in compatible with existing usage:
 *   <Tooltip content="Help text" side="top"><button>Hover me</button></Tooltip>
 */
interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

const Tooltip = React.forwardRef<HTMLButtonElement, TooltipProps>(
  ({ content, children, side = 'top', className, ...rest }, ref) => {
    if (!content) return <>{children}</>;

    return (
      <TooltipProvider delayDuration={200}>
        <TooltipRoot>
          <TooltipTrigger asChild ref={ref} {...rest}>
            {children}
          </TooltipTrigger>
          <TooltipContent side={side} className={className}>
            {content}
          </TooltipContent>
        </TooltipRoot>
      </TooltipProvider>
    );
  }
);
Tooltip.displayName = 'Tooltip';

export { Tooltip, TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent };
